import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx'

import type { NotificationCategory, ClientAppType } from '@campai/lib-common/entities'

import { client, isTRPCClientAbortError, type RouterOutput } from '@/utils/trpc'

import type { WatchCounter } from './WatchCounters'

type ListNotificationsResult = RouterOutput['currentUser']['notifications']['listNotifications']
type NotificationCursor = NonNullable<ListNotificationsResult['firstCursor']>
type NotificationRow = ListNotificationsResult['notifications'][0]

type CounterSnapshot = {
    reconcileRevision: number
    revision: string
}

type NotificationsParams = {
    categories: NotificationCategory[]
    organizationId: string | null
    queryScope: string
}

const notificationsPageSize = 50

const getCounterSnapshot = ({
    counter,
    reconcileRevision,
    updatedAt,
}: WatchCounter): CounterSnapshot => ({
    reconcileRevision,
    revision: `${updatedAt?.getTime() ?? 'none'}:${counter}:${reconcileRevision}`,
})

const getNotificationId = (notification: NotificationRow) => notification._id.toString()

export class Notifications {
    data: NotificationRow[] | null = null
    count: number | null = null
    isLoading = false

    private abortController: AbortController | null = null
    private generation = 0
    private loadedSnapshot: (CounterSnapshot & { queryScope: string }) | null = null
    private newestCursor: NotificationCursor | null = null
    private params: NotificationsParams | null = null
    private readonly getWatchCounter: () => WatchCounter
    private readonly target: ClientAppType

    constructor({
        getWatchCounter,
        target,
    }: {
        getWatchCounter: () => WatchCounter
        target: ClientAppType
    }) {
        this.getWatchCounter = getWatchCounter
        this.target = target

        makeObservable(this, {
            data: observable.ref,
            count: observable,
            isLoading: observable,
            hasMore: computed,
            start: action,
            stop: action,
            updateData: action,
        })

        reaction(
            () => this.getWatchCounter().version,
            () => void this.synchronize()
        )
    }

    get hasMore() {
        return this.data !== null && this.count !== null && this.data.length < this.count
    }

    start = ({
        categories,
        organizationId,
    }: {
        categories: readonly NotificationCategory[]
        organizationId: string | null
    }) => {
        const queryScope = JSON.stringify([this.target, organizationId, categories])
        if (this.params?.queryScope === queryScope) {
            return
        }

        this.params = {
            categories: [...categories],
            organizationId,
            queryScope,
        }
        void this.reload()
    }

    stop = () => {
        this.generation += 1
        this.abortController?.abort()
        this.abortController = null
        this.params = null
        this.loadedSnapshot = null
        this.newestCursor = null
        this.data = null
        this.count = null
        this.isLoading = false
    }

    reload = async () => {
        const params = this.params
        if (!params) {
            return
        }

        const requestedSnapshot = getCounterSnapshot(this.getWatchCounter())
        const { abortController, generation } = this.beginOperation({ clearData: true })

        try {
            const result = await client.currentUser.notifications.listNotifications.query(
                {
                    organizationId: params.organizationId,
                    targets: [this.target],
                    categories: params.categories,
                    offset: 0,
                    returnCount: true,
                },
                { signal: abortController.signal }
            )

            if (generation !== this.generation || params.queryScope !== this.params?.queryScope) {
                return
            }

            runInAction(() => {
                this.data = result.notifications
                this.count = result.count
                this.newestCursor = result.firstCursor
                this.loadedSnapshot = {
                    ...requestedSnapshot,
                    queryScope: params.queryScope,
                }
            })
        } catch (error) {
            if (generation === this.generation && !isTRPCClientAbortError(error)) {
                console.error('Failed to load notifications', error)
            }
        } finally {
            this.finishOperation(generation)
        }
    }

    loadMore = async () => {
        const currentData = this.data
        const params = this.params
        if (!currentData || !params || this.isLoading || !this.hasMore) {
            return
        }

        const { abortController, generation } = this.beginOperation()

        try {
            const result = await client.currentUser.notifications.listNotifications.query(
                {
                    organizationId: params.organizationId,
                    targets: [this.target],
                    categories: params.categories,
                    offset: currentData.length,
                    returnCount: false,
                },
                { signal: abortController.signal }
            )

            if (generation !== this.generation || params.queryScope !== this.params?.queryScope) {
                return
            }

            runInAction(() => {
                this.data = [...currentData, ...result.notifications]
            })
        } catch (error) {
            if (generation === this.generation && !isTRPCClientAbortError(error)) {
                console.error('Failed to load more notifications', error)
            }
        } finally {
            this.finishOperation(generation)
        }
    }

    updateData = ({ newData, newCount }: { newData: NotificationRow[]; newCount?: number }) => {
        this.data = newData.slice()
        if (newCount !== undefined) {
            this.count = newCount
        }
    }

    private beginOperation({ clearData = false }: { clearData?: boolean } = {}) {
        const generation = ++this.generation
        this.abortController?.abort()

        const abortController = new AbortController()
        this.abortController = abortController

        runInAction(() => {
            this.isLoading = true
            if (clearData) {
                this.data = null
                this.count = null
                this.loadedSnapshot = null
                this.newestCursor = null
            }
        })

        return { abortController, generation }
    }

    private finishOperation(generation: number, synchronize = true) {
        if (generation !== this.generation) {
            return
        }

        runInAction(() => {
            this.abortController = null
            this.isLoading = false
        })

        if (synchronize) {
            void this.synchronize()
        }
    }

    private async synchronize() {
        const currentData = this.data
        const params = this.params
        if (!currentData || !params || this.isLoading) {
            return
        }

        const currentSnapshot = getCounterSnapshot(this.getWatchCounter())
        const loadedSnapshot = this.loadedSnapshot
        if (
            loadedSnapshot?.queryScope === params.queryScope &&
            loadedSnapshot.revision === currentSnapshot.revision
        ) {
            return
        }

        const initialCursor = this.newestCursor
        if (
            !loadedSnapshot ||
            loadedSnapshot.queryScope !== params.queryScope ||
            !initialCursor ||
            loadedSnapshot.reconcileRevision !== currentSnapshot.reconcileRevision
        ) {
            await this.reload()
            return
        }

        const { abortController, generation } = this.beginOperation()
        const newNotifications: NotificationRow[] = []
        let cursor = initialCursor
        let newestCursor = initialCursor
        let reload = false

        try {
            while (true) {
                const result = await client.currentUser.notifications.listNotifications.query(
                    {
                        organizationId: params.organizationId,
                        targets: [this.target],
                        categories: params.categories,
                        after: {
                            lastActivityAtMs: cursor.lastActivityAtMs,
                            notificationId: cursor.notificationId.toString(),
                        },
                        limit: notificationsPageSize,
                        returnCount: false,
                    },
                    { signal: abortController.signal }
                )

                if (!result.notifications.length) {
                    break
                }

                if (!result.lastCursor) {
                    reload = true
                    break
                }

                newNotifications.push(...result.notifications)
                cursor = result.lastCursor
                newestCursor = result.lastCursor

                if (result.notifications.length < notificationsPageSize) {
                    break
                }
            }

            if (generation !== this.generation || params.queryScope !== this.params?.queryScope) {
                return
            }

            if (!reload) {
                runInAction(() => {
                    const knownIds = new Set(this.data?.map(getNotificationId))
                    const prependedNotifications = [...newNotifications].reverse().filter((notification) => {
                        const id = getNotificationId(notification)
                        if (knownIds.has(id)) {
                            return false
                        }

                        knownIds.add(id)
                        return true
                    })

                    if (prependedNotifications.length) {
                        this.data = [...prependedNotifications, ...(this.data ?? [])]
                        if (this.count !== null) {
                            this.count += prependedNotifications.length
                        }
                    }

                    this.newestCursor = newestCursor
                    this.loadedSnapshot = {
                        ...currentSnapshot,
                        queryScope: params.queryScope,
                    }
                })
            }
        } catch (error) {
            if (generation === this.generation && !isTRPCClientAbortError(error)) {
                reload = true
            }
        } finally {
            this.finishOperation(generation, !reload)
        }

        if (reload && generation === this.generation) {
            await this.reload()
        }
    }
}
